import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UUIDsEqual } from '@memberjunction/global';

/**
 * Shared service for org/channel context across all Izzy sections.
 * Persists the selected org and channel so switching between
 * Messages, Tickets, Analytics, and Administration retains the selection.
 *
 * Setters are no-ops when the value is unchanged, so sections can safely
 * write back the context they just applied without re-triggering every
 * other section's orgChanges$ subscription (no echo loops).
 */
@Injectable({ providedIn: 'root' })
export class IzzyContextService {
  private selectedOrgId$ = new BehaviorSubject<string | null>(null);
  private selectedChannelId$ = new BehaviorSubject<string | null>(null);

  get selectedOrganizationId(): string | null {
    return this.selectedOrgId$.value;
  }

  set selectedOrganizationId(id: string | null) {
    if (UUIDsEqual(this.selectedOrgId$.value, id)) {
      return;
    }
    this.selectedOrgId$.next(id);
  }

  get selectedChannelId(): string | null {
    return this.selectedChannelId$.value;
  }

  set selectedChannelId(id: string | null) {
    if (UUIDsEqual(this.selectedChannelId$.value, id)) {
      return;
    }
    this.selectedChannelId$.next(id);
  }

  get orgChanges$() {
    return this.selectedOrgId$.asObservable();
  }

  get channelChanges$() {
    return this.selectedChannelId$.asObservable();
  }
}
